<!-- 车辆折旧管理 -->
<template>
  <v-container fluid>
    <!-- 查询条件：资产编号、车牌号、资产状态、购置方式 -->
    <v-row class="query-row">
      <v-col cols="12" xl="2" lg="2" md="4" sm="6">
        <v-text-field v-model="queryForm.asset_no" label="资产编号" dense outlined clearable hide-details
          @keyup.enter="search()"></v-text-field>
      </v-col>
      <v-col cols="12" xl="2" lg="2" md="4" sm="6">
        <v-text-field v-model="queryForm.vehicle_number" label="车牌号" dense outlined clearable hide-details
          @keyup.enter="search()"></v-text-field>
      </v-col>
      <v-col cols="12" xl="2" lg="2" md="4" sm="6">
        <v-select v-model="queryForm.asset_status" :items="assetStatusOptions" item-text="label" item-value="value"
          label="资产状态" no-data-text="无" dense outlined clearable hide-details
          :menu-props="{ bottom: true, offsetY: true }">
        </v-select>
      </v-col>
      <v-col cols="12" xl="2" lg="2" md="4" sm="6">
        <v-select v-model="queryForm.purchase_type" :items="purchaseTypeOptions" item-text="label" item-value="value"
          label="购置方式" no-data-text="无" dense outlined clearable hide-details
          :menu-props="{ bottom: true, offsetY: true }">
        </v-select>
      </v-col>
      <v-col cols="12" xl="3" lg="3" md="6" sm="6">
        <a-space>
          <v-btn @click="search()" color="primary" dark tile>
            <v-icon small>mdi-magnify</v-icon>查询
          </v-btn>
          <v-btn @click="openCreate" color="success" dark tile>
            <v-icon small>mdi-plus</v-icon>新增资产
          </v-btn>
        </a-space>
      </v-col>
    </v-row>

    <v-card tile>
      <v-card-title class="d-flex align-center py-2 px-3" style="background-color: #fff;">
        <span class="text-subtitle-2">共 {{ count }} 条数据</span>
        <v-spacer></v-spacer>
        <v-menu bottom offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn small outlined v-bind="attrs" v-on="on" class="grey lighten-3">{{ limit }}/页</v-btn>
          </template>
          <v-list>
            <v-list-item v-for="item in pages" :key="item.value" @click="changeLimit(item.value)">
              <v-list-item-title>{{ item.value }}/页</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-card-title>
      <a-table size="small" :columns="headers" :data-source="pageList" row-key="_id" :pagination="false" bordered
        :locale="{ emptyText: '暂无数据' }" :scroll="{ x: width }">
        <!-- 资产编号 -->
        <template slot="asset_no" slot-scope="text">
          <span class="primary--text font-weight-bold">{{ text }}</span>
        </template>
        <!-- 车架号等宽小字体，避免过长挤压 -->
        <template slot="vin" slot-scope="text">
          <span class="text-body-2">{{ text }}</span>
        </template>
        <!-- 资产状态：1 在用 / 2 闲置 / 3 已处置 -->
        <template slot="asset_status" slot-scope="text">
          <a-tag :color="assetStatusColor(text)">{{ assetStatusLabel(text) }}</a-tag>
        </template>
        <!-- 购置方式 -->
        <template slot="purchase_type" slot-scope="text">
          {{ purchaseTypeLabel(text) }}
        </template>
        <!-- 资产原值保留两位小数 -->
        <template slot="original_value" slot-scope="text">
          <span class="font-weight-bold">{{ formatMoney(text) }}</span>
        </template>
        <!-- 折旧年限 -->
        <template slot="depreciation_years" slot-scope="text">
          {{ text }} 年
        </template>
        <!-- 备注超长省略 -->
        <template slot="remark" slot-scope="text">
          <a-tooltip v-if="text" :title="text" placement="topLeft">
            <span>{{ text }}</span>
          </a-tooltip>
        </template>
        <!-- 操作：编辑 / 删除（nowrap 防止挤压换行；列少不固定列） -->
        <template slot="action" slot-scope="text, record">
          <a-space :size="4" style="white-space: nowrap;">
            <a @click="openEdit(record)">编辑</a>
            <a-divider type="vertical" />
            <a-popconfirm :title="`确认删除资产「${record.asset_no}」?`" ok-text="确定" cancel-text="取消" placement="left"
              @confirm="removeItem(record)">
              <a class="error--text">删除</a>
            </a-popconfirm>
          </a-space>
        </template>
      </a-table>
    </v-card>

    <v-card tile class="mt-2">
      <v-pagination v-if="length > 1" v-model="page" :page="page" :length="length" total-visible="10"></v-pagination>
    </v-card>

    <!-- 新增 / 编辑资产弹窗 -->
    <v-dialog v-model="dialogVisible" max-width="900" persistent scrollable>
      <v-card>
        <v-card-title class="primary white--text">
          {{ dialogMode === "create" ? "新增资产" : "编辑资产" }}
        </v-card-title>
        <v-card-text class="pt-4">
          <v-form ref="form" v-model="formValid" :lazy-validation="true">
            <v-row>
              <v-col cols="12" md="4" sm="6">
                <v-text-field v-model="form.asset_no" label="资产编号" dense outlined required
                  :rules="[v => !!v || '请填写资产编号']"></v-text-field>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <v-text-field v-model="form.vehicle_number" label="车牌号" dense outlined required
                  :rules="[v => !!v || '请填写车牌号']"></v-text-field>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <!-- 车辆类型：常用项可选，也允许手动输入 -->
                <v-combobox v-model="form.vehicle_type" :items="vehicleTypeOptions" label="车辆类型" dense outlined
                  clearable hide-details :rules="[v => !!v || '请选择/填写车辆类型']"></v-combobox>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <v-combobox v-model="form.tonnage" :items="tonnageOptions" label="车型吨位" dense outlined clearable
                  hide-details :rules="[v => !!v || '请选择/填写车型吨位']"></v-combobox>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <!-- 车架号(VIN)通常17位，使用文本避免精度丢失 -->
                <v-text-field v-model="form.vin" label="车架号" dense outlined maxlength="17"></v-text-field>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <v-select v-model="form.asset_status" :items="assetStatusOptions" item-text="label" item-value="value"
                  label="资产状态" dense outlined hide-details required :rules="[v => !!v || '请选择资产状态']"
                  :menu-props="{ bottom: true, offsetY: true }"></v-select>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <v-menu ref="purchase_menu" v-model="purchase_menu" :close-on-content-click="false"
                  :return-value.sync="form.purchase_date" transition="scale-transition" min-width="auto" offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field v-model="form.purchase_date" label="购置日期" append-icon="mdi-calendar" readonly
                      v-bind="attrs" v-on="on" dense outlined clearable required hide-details
                      :rules="[v => !!v || '请选择购置日期']"></v-text-field>
                  </template>
                  <v-date-picker v-model="form.purchase_date" no-title scrollable locale="zh-cn"
                    @input="() => { $refs.purchase_menu.save(form.purchase_date); purchase_menu = false; }"></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <v-menu ref="operate_menu" v-model="operate_menu" :close-on-content-click="false"
                  :return-value.sync="form.operate_date" transition="scale-transition" min-width="auto" offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field v-model="form.operate_date" label="投运日期" append-icon="mdi-calendar" readonly
                      v-bind="attrs" v-on="on" dense outlined clearable required hide-details
                      :rules="[v => !!v || '请选择投运日期']"></v-text-field>
                  </template>
                  <v-date-picker v-model="form.operate_date" no-title scrollable locale="zh-cn"
                    @input="() => { $refs.operate_menu.save(form.operate_date); operate_menu = false; }"></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <v-text-field v-model.number="form.original_value" label="资产原值(元)" type="number" dense outlined required
                  :rules="[v => (v !== '' && v != null && Number(v) > 0) || '请输入大于0的资产原值']"></v-text-field>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <v-select v-model="form.purchase_type" :items="purchaseTypeOptions" item-text="label" item-value="value"
                  label="购置方式" dense outlined hide-details required :rules="[v => !!v || '请选择购置方式']"
                  :menu-props="{ bottom: true, offsetY: true }"></v-select>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <v-menu ref="entry_menu" v-model="entry_menu" :close-on-content-click="false"
                  :return-value.sync="form.entry_date" transition="scale-transition" min-width="auto" offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field v-model="form.entry_date" label="入账日期" append-icon="mdi-calendar" readonly
                      v-bind="attrs" v-on="on" dense outlined clearable required hide-details
                      :rules="[v => !!v || '请选择入账日期']"></v-text-field>
                  </template>
                  <v-date-picker v-model="form.entry_date" no-title scrollable locale="zh-cn"
                    @input="() => { $refs.entry_menu.save(form.entry_date); entry_menu = false; }"></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <v-text-field v-model.number="form.depreciation_years" label="折旧年限(年)" type="number" dense outlined
                  required
                  :rules="[v => (v !== '' && v != null && Number(v) > 0 && Number.isInteger(Number(v))) || '请输入正整数折旧年限']"></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-textarea v-model="form.remark" label="备注" dense outlined rows="2"></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text tile @click="dialogVisible = false">取消</v-btn>
          <v-btn color="primary" dark tile @click="submitForm">保存</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import _ from "underscore";

export default {
  name: "Depreciation",
  data() {
    return {
      // mock 开关：true=本地模拟数据；后端接口出来后改为 false，并打开 getdata/save/remove 里的真实请求
      useMock: true,
      // 查询条件：资产编号、车牌号、资产状态、购置方式
      queryForm: {
        asset_no: "",
        vehicle_number: "",
        asset_status: null,
        purchase_type: null,
      },
      // 资产状态：1 在用 / 2 闲置 / 3 已处置
      assetStatusOptions: [
        { label: "在用", value: 1 },
        { label: "闲置", value: 2 },
        { label: "已处置", value: 3 },
      ],
      // 购置方式：1 直接购入 / 2 融资租赁 / 3 分期付款
      purchaseTypeOptions: [
        { label: "直接购入", value: 1 },
        { label: "融资租赁", value: 2 },
        { label: "分期付款", value: 3 },
      ],
      // 车辆类型/吨位常用项（combobox 允许手动输入补充）
      vehicleTypeOptions: ["半挂牵引车", "重型载货车", "中型载货车", "轻型载货车", "仓栅式运输车"],
      tonnageOptions: ["12吨", "18吨", "30吨", "31吨", "42吨", "49吨"],
      pages: [
        { text: "20/页", value: 20 },
        { text: "50/页", value: 50 },
        { text: "100/页", value: 100 },
      ],
      headers: [
        { title: "资产编号", dataIndex: "asset_no", scopedSlots: { customRender: "asset_no" }, width: 140, align: "center" },
        { title: "车牌号", dataIndex: "vehicle_number", width: 110, align: "center" },
        { title: "车辆类型", dataIndex: "vehicle_type", width: 120, align: "center" },
        { title: "车型吨位", dataIndex: "tonnage", width: 90, align: "center" },
        { title: "车架号", dataIndex: "vin", scopedSlots: { customRender: "vin" }, width: 170, align: "center" },
        { title: "资产状态", dataIndex: "asset_status", scopedSlots: { customRender: "asset_status" }, width: 90, align: "center" },
        { title: "购置日期", dataIndex: "purchase_date", width: 110, align: "center" },
        { title: "投运日期", dataIndex: "operate_date", width: 110, align: "center" },
        { title: "资产原值(元)", dataIndex: "original_value", scopedSlots: { customRender: "original_value" }, width: 130, align: "center" },
        { title: "购置方式", dataIndex: "purchase_type", scopedSlots: { customRender: "purchase_type" }, width: 100, align: "center" },
        { title: "入账日期", dataIndex: "entry_date", width: 110, align: "center" },
        { title: "折旧年限", dataIndex: "depreciation_years", scopedSlots: { customRender: "depreciation_years" }, width: 90, align: "center" },
        { title: "备注", dataIndex: "remark", scopedSlots: { customRender: "remark" }, width: 180, align: "left", ellipsis: true },
        { title: "操作", dataIndex: "action", scopedSlots: { customRender: "action" }, width: 120, align: "center" },
      ],
      // 全量 mock 数据
      allList: [],
      // 查询过滤后的数据
      list: [],
      page: 1,
      limit: 20,
      count: 0,
      length: 0,
      // 新增/编辑弹窗
      dialogVisible: false,
      dialogMode: "create", // create / edit
      formValid: false,
      form: this.emptyForm(),
      purchase_menu: false,
      operate_menu: false,
      entry_menu: false,
    };
  },
  computed: {
    width() {
      return _.reduce(this.headers, (a, b) => a + (b.width || 120), 0);
    },
    // 当前页数据（前端分页）
    pageList() {
      const start = (this.page - 1) * this.limit;
      return this.list.slice(start, start + this.limit);
    },
  },
  mounted() {
    this.getdata();
  },
  methods: {
    emptyForm() {
      return {
        _id: "",
        asset_no: "",
        vehicle_number: "",
        vehicle_type: "",
        tonnage: "",
        vin: "",
        asset_status: 1,
        purchase_date: "",
        operate_date: "",
        original_value: "",
        purchase_type: 1,
        entry_date: "",
        depreciation_years: 5,
        remark: "",
      };
    },
    formatMoney(v) {
      return v == null || v === "" ? "0.00" : Number(v).toFixed(2);
    },
    assetStatusLabel(v) {
      const item = this.assetStatusOptions.find((it) => it.value === v);
      return item ? item.label : "--";
    },
    assetStatusColor(v) {
      return { 1: "green", 2: "orange", 3: "red" }[v] || "default";
    },
    purchaseTypeLabel(v) {
      const item = this.purchaseTypeOptions.find((it) => it.value === v);
      return item ? item.label : "--";
    },
    search() {
      this.page = 1;
      this.getdata();
    },
    getdata() {
      if (this.useMock) {
        this.getMockData();
        return;
      }
      // TODO 接口联调：POST /depreciation/list
      // 参数：asset_no 资产编号(模糊)；vehicle_number 车牌号(模糊)；asset_status 资产状态；purchase_type 购置方式
      this.$toast.loading({ message: "" });
      this.$api.doAPI("/depreciation/list", { page: this.page, limit: this.limit, ...this.queryForm }).then((res) => {
        this.$toast.clear();
        if (res.code === 0) {
          this.list = res.data.list;
          this.count = res.data.count;
          this.length = res.data.length || Math.max(1, Math.ceil(this.count / this.limit));
        }
        else {
          this.$alert(res.msg);
        }
      }).catch((err) => { this.$toast.clear(); console.error(err); });
    },
    // 模拟接口数据
    getMockData() {
      if (!this.allList.length) {
        const raw = [
          { asset_no: "ZC-2025-0001", vehicle_number: "豫A37230D", vehicle_type: "半挂牵引车", tonnage: "49吨", vin: "LFWSRXSJ9L1A00123", asset_status: 1, purchase_date: "2024-03-12", operate_date: "2024-03-20", original_value: 385000, purchase_type: 1, entry_date: "2024-03-15", depreciation_years: 5, remark: "主跑郑州-广州专线" },
          { asset_no: "ZC-2025-0002", vehicle_number: "豫A37301D", vehicle_type: "半挂牵引车", tonnage: "42吨", vin: "LFWSRXSJ7K2B00456", asset_status: 1, purchase_date: "2023-11-05", operate_date: "2023-11-12", original_value: 362000, purchase_type: 2, entry_date: "2023-11-08", depreciation_years: 5, remark: "融资租赁，租期36个月" },
          { asset_no: "ZC-2024-0017", vehicle_number: "豫A37150D", vehicle_type: "重型载货车", tonnage: "31吨", vin: "LGAX4C432L0C00789", asset_status: 2, purchase_date: "2022-06-18", operate_date: "2022-06-25", original_value: 298000, purchase_type: 1, entry_date: "2022-06-20", depreciation_years: 5, remark: "待维修，暂时闲置" },
          { asset_no: "ZC-2023-0009", vehicle_number: "豫A37668D", vehicle_type: "仓栅式运输车", tonnage: "18吨", vin: "LVBV7PEC8LJ00321", asset_status: 1, purchase_date: "2025-08-28", operate_date: "2025-09-02", original_value: 215000, purchase_type: 3, entry_date: "2025-08-30", depreciation_years: 4, remark: "分期付款24期" },
          { asset_no: "ZC-2021-0031", vehicle_number: "豫A38821D", vehicle_type: "半挂牵引车", tonnage: "49吨", vin: "LFWSRXRJXL1D00654", asset_status: 3, purchase_date: "2020-05-10", operate_date: "2020-05-16", original_value: 348000, purchase_type: 1, entry_date: "2020-05-12", depreciation_years: 4, remark: "2026年6月已出售处置" },
          { asset_no: "ZC-2025-0008", vehicle_number: "豫A39076D", vehicle_type: "中型载货车", tonnage: "12吨", vin: "LFNA4LBA9LAC00987", asset_status: 1, purchase_date: "2025-02-14", operate_date: "2025-02-20", original_value: 186000, purchase_type: 1, entry_date: "2025-02-16", depreciation_years: 5, remark: "" },
        ];
        this.allList = raw.map((it, i) => ({
          _id: "dep_" + (i + 1),
          ...it,
        }));
      }
      // 条件过滤：资产编号 + 车牌号 + 资产状态 + 购置方式
      let result = [...this.allList];
      if (this.queryForm.asset_no) {
        const kw = this.queryForm.asset_no.trim();
        result = result.filter((it) => (it.asset_no || "").includes(kw));
      }
      if (this.queryForm.vehicle_number) {
        const kw = this.queryForm.vehicle_number.trim();
        result = result.filter((it) => (it.vehicle_number || "").includes(kw));
      }
      if (this.queryForm.asset_status) {
        result = result.filter((it) => it.asset_status === this.queryForm.asset_status);
      }
      if (this.queryForm.purchase_type) {
        result = result.filter((it) => it.purchase_type === this.queryForm.purchase_type);
      }
      this.list = result;
      this.count = result.length;
      this.length = Math.max(1, Math.ceil(this.count / this.limit));
      if (this.page > this.length) this.page = 1;
    },
    changeLimit(v) {
      this.limit = v;
      this.page = 1;
      this.getdata();
    },
    openCreate() {
      this.form = this.emptyForm();
      this.dialogMode = "create";
      this.dialogVisible = true;
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
    },
    openEdit(record) {
      this.form = { ...this.emptyForm(), ..._.pick(record, Object.keys(this.emptyForm())) };
      this.dialogMode = "edit";
      this.dialogVisible = true;
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
    },
    submitForm() {
      this.$refs.form.validate((valid) => {
        if (!valid) return;
        if (this.useMock) {
          this.saveMock();
          return;
        }
        // TODO 接口联调：POST /depreciation/save（新增/编辑，编辑传 _id）
        // this.$toast.loading({ message: "" });
        this.$api.doAPI("/depreciation/save", { ...this.form }).then((res) => {
          this.$toast.clear();
          if (res.code === 0) {
            this.$message.success(this.dialogMode === "create" ? "新增成功" : "已更新");
            this.dialogVisible = false;
            this.getdata();
          }
          else {
            this.$alert(res.msg);
          }
        }).catch((err) => { this.$toast.clear(); console.error(err); });
      });
    },
    saveMock() {
      const data = {
        ...this.form,
        original_value: Math.round(Number(this.form.original_value) * 100) / 100,
        depreciation_years: Number(this.form.depreciation_years),
      };
      if (this.dialogMode === "create") {
        this.allList = [{ ...data, _id: "dep_" + Date.now() }, ...this.allList];
        this.$message.success("新增成功");
      }
      else {
        this.allList = this.allList.map((it) => (it._id === data._id ? { ...it, ...data } : it));
        this.$message.success("已更新");
      }
      this.dialogVisible = false;
      this.getdata();
    },
    removeItem(record) {
      if (this.useMock) {
        this.allList = this.allList.filter((it) => it._id !== record._id);
        this.$message.success("已删除");
        this.getdata();
        return;
      }
      // TODO 接口联调：POST /depreciation/remove
      // 参数：_id 资产ID
      this.$toast.loading({ message: "" });
      this.$api.doAPI("/depreciation/remove", { _id: record._id }).then((res) => {
        this.$toast.clear();
        if (res.code === 0) {
          this.$message.success("已删除");
          this.getdata();
        }
        else {
          this.$alert(res.msg);
        }
      }).catch((err) => { this.$toast.clear(); console.error(err); });
    },
  },
};
</script>
<style lang="less" scoped>
/deep/ .row {
  margin-bottom: 0 !important;
}

/* 查询条件行与下方表格保持间距 */
/deep/ .row.query-row {
  margin-bottom: 16px !important;
}

/deep/ .col,
.col-12 {
  padding-bottom: 0 !important;
}

/deep/ .v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
}

p {
  margin: 0;
}
</style>
